// The layouts of three detonation looks that fill a shape (R2079,
// `ui-map-interface.md` §6.3): the shards of card 04 SHATTERED DISC, the
// ragged boil of card 07 DEPTH CHARGE BOIL and the hex lattice of card 08
// HEX STAMP, of `archive/mockups/065-torpedo-arrivals.html`.
//
// A layout is pure arithmetic of the drawn radius, the weight and the seed,
// line for line the card's `shards04`, `edge07` and `hexes08`. Two readers
// take it: the recipe that moves the pieces each frame, and
// `detonation-cells.ts`, which bakes each filled shape into the atlas,
// because the batches fill no polygon. Both build it from the same three
// figures, so a cell and the strokes round it agree to the bit.
//
// The weight is given in thousandths (`pwQ`), the figure an atlas key can
// carry, and both readers build from it.
//
// This is a leaf (`leaves.test.ts`).

import { mulberry32 } from '../world';
import { TAU, lerp } from './recipe-kit';

// ---------------------------------------------------------------------------
// Card 04, the shards
// ---------------------------------------------------------------------------

/** One shard: its outline, its centroid, the centroid's distance from the
 *  impact, two random shares and the ring it stands in. */
export interface Shard {
  pts: readonly (readonly [number, number])[];
  cx: number;
  cy: number;
  d: number;
  r1: number;
  r2: number;
  ring: number;
}

/** The shard cell size: about 24 px at no weight, 10 px at full weight. */
const SHARD_CELL_LIGHT_PX = 24;
const SHARD_CELL_HEAVY_PX = 10;
/** The smallest shard cell, and its share of the radius. */
const SHARD_CELL_MIN_PX = 4;
const SHARD_CELL_SHARE = 0.55;
/** How far a band's edge wanders, as a share of the band. */
const SHARD_JITTER_SHARE = 0.3;
/** The point the inner shards meet at wanders this share of a band. */
const SHARD_CORE_SHARE = 0.35;
/** The length of a straight piece of a shard's curved edge, in px. */
const SHARD_STEP_PX = 5;

const shardSets = new Map<string, readonly Shard[]>();

/** The shards of card 04 for a drawn radius, a weight and a seed: the
 *  card's `shards04`, once a set. */
export function shardsOf(R: number, pwQ: number, seed: number): readonly Shard[] {
  const key = `${R}:${pwQ}:${seed}`;
  let out = shardSets.get(key);
  if (out === undefined) {
    out = buildShards(R, pwQ / 1000, seed);
    shardSets.set(key, out);
  }
  return out;
}

function buildShards(R: number, pw: number, seed: number): Shard[] {
  const rnd = mulberry32(seed + 404);
  const cell = Math.min(lerp(SHARD_CELL_LIGHT_PX, SHARD_CELL_HEAVY_PX, pw), Math.max(SHARD_CELL_MIN_PX, R * SHARD_CELL_SHARE));
  const nr = Math.max(1, Math.round(R / cell)), band = R / nr;
  const bnd: { r: number; a1: number; a2: number; j: number }[] = [];
  for (let i = 0; i <= nr; i++) {
    bnd.push({ r: band * i, a1: rnd() * TAU, a2: rnd() * TAU, j: (i > 0 && i < nr) ? band * SHARD_JITTER_SHARE : 0 });
  }
  const rad = (i: number, a: number): number => {
    const b = bnd[i];
    return b.r + b.j * (0.6 * Math.sin(3 * a + b.a1) + 0.4 * Math.sin(7 * a + b.a2));
  };
  const at = (i: number, a: number): [number, number] => {
    const r = rad(i, a);
    return [Math.cos(a) * r, Math.sin(a) * r];
  };
  const c0: [number, number] = [(rnd() - 0.5) * band * SHARD_CORE_SHARE, (rnd() - 0.5) * band * SHARD_CORE_SHARE];
  const out: Shard[] = [];
  for (let i = 0; i < nr; i++) {
    const rm = band * (i + 0.5);
    const s = i === 0 ? Math.max(4, Math.round(4 + 3 * pw)) : Math.max(5, Math.round(TAU * rm / cell * 1.05));
    const off = rnd() * TAU, ci: number[] = [], co: number[] = [];
    for (let k = 0; k < s; k++) {
      const a = off + (k + (rnd() - 0.5) * 0.7) * TAU / s;
      ci.push(a); co.push(a + (rnd() - 0.5) * 0.55 * TAU / s);
    }
    for (let k = 0; k < s; k++) {
      const i0 = ci[k], i1 = k + 1 < s ? ci[k + 1] : ci[0] + TAU;
      const o0 = co[k], o1 = k + 1 < s ? co[k + 1] : co[0] + TAU;
      const pts: [number, number][] = [];
      if (i === 0) pts.push(c0);
      else {
        const n = Math.max(1, Math.ceil((i1 - i0) * rad(i, i0) / SHARD_STEP_PX));
        for (let q = 0; q <= n; q++) pts.push(at(i, lerp(i0, i1, q / n)));
      }
      const n = Math.max(2, Math.ceil((o1 - o0) * rad(i + 1, o0) / SHARD_STEP_PX));
      for (let q = n; q >= 0; q--) pts.push(at(i + 1, lerp(o0, o1, q / n)));
      let cx = 0, cy = 0;
      for (const v of pts) { cx += v[0]; cy += v[1]; }
      cx /= pts.length; cy /= pts.length;
      out.push({ pts, cx, cy, d: Math.hypot(cx, cy), r1: rnd(), r2: rnd(), ring: i });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Card 07, the boil
// ---------------------------------------------------------------------------

/** One foam fleck: its bearing, its share of the edge, its size share, its
 *  start share and its sway phase. */
export interface Fleck { a: number; d: number; s: number; ts: number; ph: number }

/** One spray drop: its bearing, its share of the radius, its size share
 *  and its start share. */
export interface Spray { a: number; d: number; s: number; ts: number }

/** The ragged edge of card 07: `M` notches of depth `n[k]` (0 on the rim,
 *  1 deepest), the depth share `amp`, the flecks and the spray. */
export interface Boil {
  M: number;
  n: readonly number[];
  amp: number;
  flecks: readonly Fleck[];
  spray: readonly Spray[];
}

/** The rim length of one notch, in px. */
const BOIL_NOTCH_PX = 4.5;
/** Every fifth notch touches the rim, so the boil lands on R. */
const BOIL_RIM_EVERY = 5;
/** The deepest notch, as a share of R: deeper under a radius of 30. */
const BOIL_AMP_SMALL = 0.2;
const BOIL_AMP = 0.13;
const BOIL_SMALL_R_PX = 30;
/** The fleck count: a base, a count a px of radius, and a ceiling. */
const BOIL_FLECKS_BASE = 10;
const BOIL_FLECKS_PER_PX = 1.4;
const BOIL_FLECKS_MAX = 220;
/** The spray count: a base and a count a px of radius. */
const BOIL_SPRAY_BASE = 6;
const BOIL_SPRAY_PER_PX = 1.2;

const boils = new Map<string, Boil>();

/** The boil of card 07 for a drawn radius and a seed: the card's
 *  `edge07`, once a set. */
export function boilOf(R: number, seed: number): Boil {
  const key = `${R}:${seed}`;
  let b = boils.get(key);
  if (b === undefined) {
    b = buildBoil(R, seed);
    boils.set(key, b);
  }
  return b;
}

function buildBoil(R: number, seed: number): Boil {
  const rnd = mulberry32(seed + 707), M = Math.max(14, Math.round(TAU * R / BOIL_NOTCH_PX));
  const raw: number[] = [];
  for (let k = 0; k < M; k++) raw.push(rnd());
  let n = raw.map((v, k) => (v + raw[(k + 1) % M] + raw[(k + M - 1) % M]) / 3);
  const lo = Math.min(...n), hi = Math.max(...n);
  n = n.map((v) => (v - lo) / (hi - lo || 1));
  for (let k = 0; k < M; k += BOIL_RIM_EVERY) n[k] = 0;
  const flecks: Fleck[] = [], nf = Math.min(BOIL_FLECKS_MAX, Math.round(BOIL_FLECKS_BASE + R * BOIL_FLECKS_PER_PX));
  for (let i = 0; i < nf; i++) flecks.push({ a: rnd() * TAU, d: Math.sqrt(rnd()), s: rnd(), ts: rnd(), ph: rnd() * TAU });
  const spray: Spray[] = [], ns = Math.round(BOIL_SPRAY_BASE + R * BOIL_SPRAY_PER_PX);
  for (let i = 0; i < ns; i++) spray.push({ a: rnd() * TAU, d: 0.72 + rnd() * 0.22, s: rnd(), ts: rnd() });
  return { M, n, amp: R < BOIL_SMALL_R_PX ? BOIL_AMP_SMALL : BOIL_AMP, flecks, spray };
}

/** The radius of the boil's edge at bearing `a`: the card's `edgeR07`. */
export function boilEdge(b: Boil, R: number, a: number): number {
  const f = ((((a % TAU) + TAU) % TAU) / TAU) * b.M, k = Math.floor(f) % b.M, u = f - Math.floor(f);
  return R * (1 - b.amp * lerp(b.n[k], b.n[(k + 1) % b.M], u));
}

/** The middle of the card's notch swing `0.75 + 0.25 · sin(…)`: the depth
 *  the baked boil cell takes, while the stroke round it swings. */
export const BOIL_SWING_MID = 0.75;
/** The swing of a notch about that middle, and its rate and phase step. */
export const BOIL_SWING = 0.25;
export const BOIL_SWING_RATE = 7;
export const BOIL_SWING_STEP = 1.7;

/** The radius of notch `k` of the boil at scale `s`, its depth swung by
 *  `swing` (the card's `0.75 + 0.25 · sin(t · 7 + k · 1.7)`). */
export function boilNotch(b: Boil, R: number, k: number, s: number, swing: number): number {
  return R * (1 - b.amp * b.n[k] * swing) * s;
}

// ---------------------------------------------------------------------------
// Card 08, the hexes
// ---------------------------------------------------------------------------

/** One hex of the lattice: its centre, its distance and a random share. */
export interface Hex { x: number; y: number; d: number; j: number }

/** The hex lattice of card 08: the hex radius, the hexes nearest first and
 *  the farthest distance. */
export interface HexSet { h: number; list: readonly Hex[]; dmax: number }

/** The hex radius: R / 5, held to 1.5 to 7 px. */
const HEX_SHARE = 1 / 5;
const HEX_MIN_PX = 1.5;
const HEX_MAX_PX = 7;

const hexSets = new Map<string, HexSet>();

/** The hexes of card 08 for a drawn radius and a seed: the card's
 *  `hexes08`, once a set. No hex crosses R. */
export function hexesOf(R: number, seed: number): HexSet {
  const key = `${R}:${seed}`;
  let s = hexSets.get(key);
  if (s === undefined) {
    s = buildHexes(R, seed);
    hexSets.set(key, s);
  }
  return s;
}

function buildHexes(R: number, seed: number): HexSet {
  const h = Math.max(HEX_MIN_PX, Math.min(HEX_MAX_PX, R * HEX_SHARE)), dx = Math.sqrt(3) * h, dy = 1.5 * h;
  const rnd = mulberry32(seed + 808), out: Hex[] = [];
  const nrow = Math.ceil(R / dy) + 1, ncol = Math.ceil(R / dx) + 1;
  for (let row = -nrow; row <= nrow; row++) {
    for (let col = -ncol; col <= ncol; col++) {
      const x = (col + (Math.abs(row) % 2) * 0.5) * dx, y = row * dy;
      let fits = true;
      for (let k = 0; k < 6; k++) {
        const a = ((60 * k - 30) * Math.PI) / 180;
        if (Math.hypot(x + Math.cos(a) * h, y + Math.sin(a) * h) > R + 1e-6) { fits = false; break; }
      }
      if (fits) out.push({ x, y, d: Math.hypot(x, y), j: rnd() });
    }
  }
  out.sort((a, b) => a.d - b.d);
  return { h, list: out, dmax: out.length ? out[out.length - 1].d : 1 };
}
